import React, { useState } from 'react';

const TaskModal = ({ pets, caregivers, onClose, onSave, taskToEdit }) => {
  const isEditMode = !!taskToEdit;
  const [selectedPet, setSelectedPet] = useState(taskToEdit?.petId || '');
  const [activities, setActivities] = useState(
    taskToEdit?.activities || [
      { type: 'paseo', time: '', priority: 'medium', status: 'pending', caregiver: '', notes: '' }
    ]
  );

  const handleActivityChange = (index, field, value) => {
    const updatedActivities = [...activities];
    updatedActivities[index] = {
      ...updatedActivities[index],
      [field]: value
    };
    setActivities(updatedActivities);
  };

  const addNewActivity = () => {
    setActivities([
      ...activities,
      { type: 'paseo', time: '', priority: 'medium', status: 'pending', caregiver: '', notes: '' }
    ]);
  };

  const removeActivity = (index) => {
    if (activities.length > 1) {
      const updatedActivities = [...activities];
      updatedActivities.splice(index, 1);
      setActivities(updatedActivities);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const taskData = {
      petId: selectedPet,
      activities: activities.map(activity => ({
        ...activity,
        time: activity.time || '00:00'
      }))
    };
    onSave(taskData, isEditMode ? taskToEdit.id : null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{isEditMode ? 'Editar Tarea' : 'Nueva Tarea'}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Mascota</label>
            <select
              value={selectedPet}
              onChange={(e) => setSelectedPet(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            >
              <option value="">Seleccionar mascota</option>
              {pets.map(pet => (
                <option key={pet.id} value={pet.id}>{pet.name}</option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Actividades</label>
              <button
                type="button"
                onClick={addNewActivity}
                className="text-sm text-cyan-600 hover:text-cyan-800"
              >
                + Añadir actividad
              </button>
            </div>

            {activities.map((activity, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg mb-3">
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => removeActivity(index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Eliminar
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                    <select
                      value={activity.type}
                      onChange={(e) => handleActivityChange(index, 'type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="paseo">Paseo</option>
                      <option value="comida">Comida</option>
                      <option value="medicación">Medicación</option>
                      <option value="juego">Juego</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                    <input
                      type="time"
                      value={activity.time}
                      onChange={(e) => handleActivityChange(index, 'time', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
                    <select
                      value={activity.priority}
                      onChange={(e) => handleActivityChange(index, 'priority', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="high">Alta</option>
                      <option value="medium">Media</option>
                      <option value="low">Baja</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cuidador</label>
                    <select
                      value={activity.caregiver}
                      onChange={(e) => handleActivityChange(index, 'caregiver', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Seleccionar</option>
                      {caregivers.map(caregiver => (
                        <option key={caregiver.id} value={caregiver.name}>{caregiver.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                    <textarea
                      value={activity.notes || ''}
                      onChange={(e) => handleActivityChange(index, 'notes', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      rows="2"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
            >
              {isEditMode ? 'Actualizar' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskModal;